import { View } from "ui/atoms/view";
import { Text } from "react-native";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { useCreateBodyMapEntry } from "../hooks/useCreateBodyMapEntry";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { Button, ButtonText } from "ui/gluestack/button";
import {
  requiredValidation,
  validateFileExtensions,
} from "ui/utils/Validators";
import UploadDocumentArea from "ui/organisms/UploadDocumentArea";
import { generalAllowedExtensions } from "../../../constants/allowedExtensionsConstants";
import { Controller } from "react-hook-form";
import { useUploadBodyMapEntryMedia } from "../hooks/useUploadBodyMapEntryMedia";
import { useState } from "react";
import { ThrelsFileType } from "ui/types/ThrelsFileType";

interface Props {
  selectedBodyParts: string[];
  onClose: () => void;
}

export default function CreateBodyMapEntryForm({
  selectedBodyParts,
  onClose,
}: Props) {
  const [image, setImage] = useState<ThrelsFileType | null | undefined>(null);

  const {
    control: createBodyMapEntryControl,
    submit: createBodyMapEntrySubmit,
    isSubmitting,
  } = useCreateBodyMapEntry({
    selectedBodyParts,
    onSuccess: async (entry) => {
      try {
        if (image) {
          await uploadBodyMapMediaSubmit({
            residentId: entry.resident_id,
            entryId: entry.id,
            initialComment: entry.initial_comment,
            image,
          });
        }
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    control: uploadBodyMapMediaControl,
    submit: uploadBodyMapMediaSubmit,
    isLoading: isUploadingMedia,
  } = useUploadBodyMapEntryMedia();

  return (
    <View>
      <View className="gap-3">
        <Text className="text-base font-normal text-typography-900">
          Condition
        </Text>
        <FormControlledInput
          id="condition"
          name="condition"
          control={createBodyMapEntryControl}
          placeholder="Enter condition"
        />
        <Text className="text-base font-normal text-typography-900">
          More Information
        </Text>
        <FormControlledInputTextArea
          id="initial_comment"
          name="initial_comment"
          control={createBodyMapEntryControl}
          placeholder="Enter relevant information about the areas marked"
          className="text-gray-500"
          isRequired
          rules={{
            required: requiredValidation("More Information field"),
          }}
        />
        <Controller
          control={uploadBodyMapMediaControl}
          name="image"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <UploadDocumentArea
              value={value}
              onChange={(files) => {
                onChange(files);
                setImage(files && files.length > 0 ? files[0] : null);
              }}
              allowedExtensions={generalAllowedExtensions}
              errorMessage={error}
              singleFile
            />
          )}
          rules={{
            validate: validateFileExtensions(generalAllowedExtensions),
          }}
        />
      </View>
      <View className="border-b border-gray-200 w-full my-8" />
      <View className="gap-3">
        <Text className="text-base font-normal text-typography-900">
          Treatment Suggested
        </Text>
        <FormControlledInputTextArea
          id="treatment_plan"
          name="treatment_plan"
          control={createBodyMapEntryControl}
          placeholder="Enter suggested treatments"
          className="text-gray-500 "
        />
        <Text className="text-base font-normal text-typography-900">
          Task Frequency
        </Text>
        <FormControlledInput
          id="task_frequency"
          name="task_frequency"
          control={createBodyMapEntryControl}
          placeholder="Enter treatment frequency"
        />
      </View>
      <View className="col-span-full">
        <View className="flex justify-end gap-4 sm:flex-row sm:gap-x-4 mt-10">
          <Button
            action="primary"
            size="sm"
            variant="outline"
            onPress={onClose}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            action="primary"
            size="sm"
            onPress={createBodyMapEntrySubmit}
            disabled={isSubmitting || isUploadingMedia}
          >
            <ButtonText className="text-white">Log Finding</ButtonText>
          </Button>
        </View>
      </View>
    </View>
  );
}
